import { useAuth } from "../../context/AuthContext";
import {
  useInstructorTier,
  DEV_TIER_OVERRIDE_KEY,
  DEV_TIER_EVENT,
} from "../../hooks/useInstructorTier";
import { ChevronDown, FlaskConical } from "lucide-react";
import { MobileMenu } from "../mobile";
import { isLiveProductionHost } from "../../lib/env";

/**
 * TierSwitcher — Dev-only tier preview control. Feb 6, 2026.
 *
 * Lets admin users preview the app as Basic / Premium / Growth
 * without changing their real billing. Writes
 * `localStorage.dev_tier_override` and dispatches a custom event so
 * every component using `useInstructorTier()` re-renders instantly.
 *
 * Hard-gated to admins by role check. Non-admins never see this UI
 * AND can't trigger the override even if they craft the localStorage
 * key — `useInstructorTier` re-validates role on every read.
 *
 * Removal: delete this file + its mount in App.js. Hook keeps the
 * override key constant so any leftover localStorage is silently
 * ignored once role is no longer admin.
 *
 * NOT a billing change. NOT persisted server-side. NOT visible to
 * real users.
 */

const OPTIONS = [
  { key: null,       label: "Real plan",  hint: "Use my actual subscription" },
  { key: "basic",    label: "Basic",      hint: "£19 — core tools only" },
  { key: "premium",  label: "Premium",    hint: "+ AI structure tools" },
  { key: "growth",   label: "Growth",     hint: "+ gap recovery, recurring income, analytics" },
];

export default function TierSwitcher() {
  const { user } = useAuth();
  const { devOverride, realTier, planLabel } = useInstructorTier();

  // Aug 2026 · Production Stabilisation — dev/test surfaces must
  // never appear on the live production host. Hide unconditionally
  // when the app is running against nationaldrivingguild.co.uk.
  if (isLiveProductionHost()) return null;

  // Hard gate: admin only. The hook's own re-validation is belt;
  // this is braces.
  if ((user?.role || "").toLowerCase() !== "admin") return null;

  // May 14, 2026 — only show the plan switcher in contexts where plan
  // gating is actually visible. On /admin* the command centre has no
  // tier-gated tiles so the button was confusing ("nothing happened").
  // It still surfaces inside instructor preview shells where the
  // gating IS rendered. Admins can switch role via AdminRoleSwitcher
  // first, then use this on the previewed shell.
  const inInstructorPreview = !!(
    user?.operating_mode && (
      user.operating_mode.endsWith("_adi") ||
      user.operating_mode.endsWith("_pdi")
    )
  );
  if (!inInstructorPreview) return null;

  const setOverride = (value) => {
    try {
      if (value) localStorage.setItem(DEV_TIER_OVERRIDE_KEY, value);
      else localStorage.removeItem(DEV_TIER_OVERRIDE_KEY);
    } catch {}
    // Same-tab listeners don't fire on storage events; dispatch a
    // custom event so the hook subscribers re-render.
    try { window.dispatchEvent(new Event(DEV_TIER_EVENT)); } catch {}
  };

  const activeLabel = devOverride
    ? OPTIONS.find((o) => o.key === devOverride)?.label
    : "Real plan";

  return (
    <div
      data-testid="dev-tier-switcher"
      className="mx-auto mt-3 max-w-md select-none"
    >
      <MobileMenu>
        <MobileMenu.Trigger asChild>
          <button
            data-testid="dev-tier-switcher-toggle"
            className={
              "flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold shadow-lg transition-all " +
              (devOverride
                ? "bg-amber-500 text-slate-900 hover:bg-amber-400"
                : "bg-slate-900 text-white hover:bg-slate-800")
            }
            title="Dev only — preview tier"
          >
            <FlaskConical className="w-3.5 h-3.5" />
            <span>Dev · {activeLabel}</span>
            <ChevronDown className="w-3.5 h-3.5 transition-transform data-[state=open]:rotate-180" />
          </button>
        </MobileMenu.Trigger>

        <MobileMenu.Content
          testid="dev-tier-switcher-menu"
          side="top"
          align="end"
          className="w-64 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
        >
          <div className="px-3 py-2 -m-1 mb-1 border-b border-slate-100 dark:border-slate-800 bg-amber-50 dark:bg-amber-900/15">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-amber-700">Testing Mode · Admin Only</p>
            <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">
              Real plan: {planLabel} ({realTier}). Choosing below previews UI gating only — billing is not affected.
            </p>
          </div>
          {OPTIONS.map((opt) => {
            const active = devOverride === opt.key;
            return (
              <MobileMenu.Item
                key={opt.key || "real"}
                testid={`dev-tier-option-${opt.key || "real"}`}
                onSelect={() => setOverride(opt.key)}
                className={
                  "w-full text-left items-start gap-2 text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 focus:bg-slate-50 dark:focus:bg-slate-800 " +
                  (active ? "bg-amber-50 dark:bg-amber-900/20" : "")
                }
              >
                <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ${active ? "bg-amber-500" : "bg-slate-200 dark:bg-slate-700"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{opt.label}</p>
                  <p className="text-[10px] text-slate-500 leading-tight">{opt.hint}</p>
                </div>
              </MobileMenu.Item>
            );
          })}
        </MobileMenu.Content>
      </MobileMenu>
    </div>
  );
}
